import React from 'react';
import {mergeStyles} from 'components/FluentUICompat';
import {EntityListColumn as Column} from 'components';
import {
	renderParagraph,
	renderRequirements,
	renderArrayField,
	renderPhase,
	renderFaqs,
	renderLazyRequirements,
	renderChangeInfoType,
} from 'components/EntityList/ColumnRenderers';
import {KeywordFields} from 'components/Keywords/keywordItem.types';
import {getDateColumn} from 'helpers';
import {DateTimeFormat, formatDateTime} from 'i18n/localeDateFormat';
import i18next from 'i18next';
import {TFunction} from 'react-i18next';
import {
	Keyword,
	KeywordAssignment,
	PhaseDate,
	VexClusterAssignment,
} from 'types';
import {ParagraphListKeywords} from './ParagraphListKeywords';
import {parseTooltipNewlines} from 'helpers/tooltips';
import {ARRAY_TYPE} from 'components/EntityList/EntityUtils';

export const getCreateParagraphFieldWithDefaults = (filterable: boolean) => {
	return (fields: Column): Column => {
		return {
			isMultiline: true,
			isResizable: true,
			filterable,
			...fields,
		};
	};
};

interface ParagraphListItem {
	keywords: KeywordFields[];
	keywordAssignments: KeywordAssignment[];
}

interface GetParagraphListBaseColumnsOptions {
	filterable?: boolean;
	lazyLoadedTooltips?: boolean;
	shouldShowFaq?: boolean;
	myKeywords?: Keyword[];
	isReqifz?: boolean;
	excludedColumnKeys?: Set<string>; // Optimization: pass role-based exclusions to avoid creating unnecessary columns
}

export const getParagraphListBaseColumns = (
	t: TFunction,
	tt: TFunction,
	tEnum: TFunction,
	options: GetParagraphListBaseColumnsOptions = {},
): Column[] => {
	const {
		filterable = true,
		lazyLoadedTooltips = false,
		shouldShowFaq,
		myKeywords,
		isReqifz = false,
		excludedColumnKeys,
	} = options;
	const paragraphClassName = mergeStyles({
		paddingTop: '0px !important',
		paddingBottom: '0px !important',
		height: '100% !important',
	});

	/**
	 * Efficiently filters out columns using Set-based O(1) lookup instead of array indexOf O(n).
	 * Actually removes columns from the list rather than just marking them hidden,
	 * which reduces memory and rendering overhead for large column lists.
	 */
	const hideSpecificColumns = (
		columns: Column[],
		hiddenColumnKeys: Set<string>,
	): Column[] => {
		return columns.filter(c => !hiddenColumnKeys.has(c.key ?? ''));
	};

	const getExtraFieldsForColumns = (): Column[] => {
		const extraColumns = [
			{
				key: 'p-paragraph',
				name: t('Paragraph'),
				fieldName: 'name',
				minWidth: 400,
				className: paragraphClassName,
				filterable: false,
				onRender: (item: any) => renderParagraph(item, t),
			},
			{
				key: 'p-categories',
				name: t('Categories'),
				fieldName: 'categories',
				minWidth: 100,
				dataType: ARRAY_TYPE,
				onRender: renderArrayField(),
			},
			{
				key: 'p-keywords',
				name: t('Keywords'),
				fieldName: 'keywords',
				minWidth: 100,
				dataType: ARRAY_TYPE,
				onRender(item: ParagraphListItem): React.ReactElement {
					const suggestions = item?.keywordAssignments?.flatMap(
						(ka: KeywordAssignment) =>
							ka?.vexClusterAssignments?.flatMap(
								(vca: VexClusterAssignment) => vca.keywordSuggestions || [],
							),
					);
					return (
						<ParagraphListKeywords
							myKeywords={myKeywords}
							suggestions={suggestions}
							paragraphKeywords={item.keywords}
						/>
					);
				},
			},
			{
				key: 'p-requirements',
				name: t('Requirements'),
				fieldName: 'requirements',
				minWidth: 100,
				dataType: ARRAY_TYPE,
				onRender:
					lazyLoadedTooltips === true
						? renderLazyRequirements()
						: renderRequirements(),
			},
			{
				key: 'p-tags',
				name: t('Tags'),
				fieldName: 'tags',
				minWidth: 150,
				dataType: ARRAY_TYPE,
				onRender: renderArrayField(),
			},
			{
				key: 'p-vehicle-category',
				name: t('VehicleCategory'),
				fieldName: 'vehicleCategories',
				minWidth: 100,
				dataType: ARRAY_TYPE,
				onRender: renderArrayField(),
			},
			{
				key: 'p-drive-variants',
				name: t('DriveVariants'),
				fieldName: 'driveVariants',
				minWidth: 100,
				dataType: ARRAY_TYPE,
				onRender: renderArrayField(),
				tooltipHostProps: {
					content: parseTooltipNewlines(tt('driveVariants')),
				},
			},

			getDateColumn(
				'p-dateNewTypes',
				t('DateNewTypes'),
				'dateNewTypes',
				filterable,
				true,
				100,
			),
			getDateColumn(
				'p-dateNewRegistration',
				t('DateNewRegistration'),
				'dateNewRegistration',
				filterable,
				true,
				100,
			),
			getDateColumn(
				'p-comprehensive',
				t('Comprehensive'),
				'comprehensive',
				filterable,
				true,
				100,
			),
			{
				key: 'p-modelYear',
				name: t('ModelYear'),
				fieldName: 'modelYear',
				minWidth: 100,
			},
			{
				key: 'p-phaseIn',
				name: t('PhaseIn'),
				fieldName: 'phaseIn',
				minWidth: 200,
				filterable: true,
				dataType: ARRAY_TYPE,
				getFilterKey: getPhaseFilterKey,
				getFilterLabel: getPhaseFilterLabel,
				onRender: renderPhase(),
			},
			{
				key: 'p-phaseOut',
				name: t('PhaseOut'),
				fieldName: 'phaseOut',
				minWidth: 200,
				filterable: true,
				dataType: ARRAY_TYPE,
				getFilterKey: getPhaseFilterKey,
				getFilterLabel: getPhaseFilterLabel,
				onRender: renderPhase(),
			},
			{
				key: 'p-changeInfoType',
				name: t('ChangeInfoType'),
				fieldName: 'changeInfoType',
				minWidth: 100,
				filterable: true,
				onRender: renderChangeInfoType(),
			},
		];
		return extraColumns;
	};

	const getColumns = (): Column[] => {
		const extraFields: Column[] =
			shouldShowFaq === true
				? getExtraFieldsForColumns().concat({
						key: 'faq',
						name: 'Q&As',
						fieldName: 'faq',
						isMultiline: true,
						minWidth: 400,
						isResizable: true,
						filterable: true,
						onRender: renderFaqs(),
				  })
				: getExtraFieldsForColumns();
		const mappedFields = extraFields.map(
			getCreateParagraphFieldWithDefaults(filterable),
		);
		// Build excluded columns Set based on config and role
		const excludedColumnsSet = excludedColumnKeys
			? new Set(excludedColumnKeys)
			: new Set<string>();
		if (!isReqifz) {
			excludedColumnsSet.add('p-changeInfoType');
		}
		return hideSpecificColumns(mappedFields, excludedColumnsSet);
	};
	return getColumns();
};

export function getPhaseFilterKey(val: PhaseDate) {
	return `${val.date} ${val.status}`;
}

export function getPhaseFilterLabel(val: PhaseDate) {
	return `${formatDateTime(
		new Date(val.date),
		i18next,
		DateTimeFormat.DateShort,
	)} (${val.status})`;
}

export const getChangeInfoLabel = (tEnum: TFunction) => (item: string) =>
	tEnum(`ChangeInfoType.${item}`);
